import React, { useState, useEffect, useCallback } from 'react';
import { UserIdLink } from '@/components/app';
import {
  learnerApi,
  ExamAttemptListItem,
  ExamAttemptDetail,
  ExamKind,
} from '../../api/learner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Sheet, SheetBody, SheetContent, SheetHeader } from '../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ChevronRight, ClipboardList } from 'lucide-react';
import { Pagination } from '../../components/shared/Pagination';
import { DetailSection, DetailItem } from '../../components/shared/ViewDialog';
import {
  EmptyState,
  ErrorState,
  FilterChips,
  FormField,
  PageHeader,
  StatusPill,
  Toolbar,
  type FilterChip,
  type StatusTone,
} from '../../components/app';
import { ExportCsvButton } from '../../components/shared/ExportCsvButton';
import type { CsvColumn } from '../../lib/csv';
import { getErrorMessage } from '@/lib/errors';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { tashkentDayEndIso, tashkentDayStartIso } from '@/lib/datetime';
import { EXAM_KIND_LABELS, labelOf } from '@/lib/labels';

const PAGE_SIZE = 25;

/** Вид попытки — нейтральные таблетки, «Работа над ошибками» выделена тоном warn. */
const KIND_TONE: Record<ExamKind, StatusTone> = {
  mock: 'info',
  retake: 'neutral',
  wrong: 'warn',
  marathon: 'neutral',
  ticket: 'neutral',
  topic: 'neutral',
};

const KINDS: ExamKind[] = ['mock', 'retake', 'wrong', 'marathon', 'ticket', 'topic'];

const PASSED_LABELS: Record<string, string> = { true: 'Сдал', false: 'Не сдал' };

/** Итог попытки: текст + тон. */
function resultOf(a: { passed: boolean | null; abandoned: boolean }): { text: string; tone: StatusTone } {
  if (a.passed === true) return { text: 'Сдал', tone: 'pos' };
  if (a.passed === false) return { text: 'Не сдал', tone: 'neg' };
  if (a.abandoned) return { text: 'Брошено', tone: 'warn' };
  return { text: 'Не завершено', tone: 'neutral' };
}

const day = (d: string) => d.split('-').reverse().join('.');

export default function ExamAttemptsPage() {
  const [items, setItems] = useState<ExamAttemptListItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState<string>('all');
  const [passedFilter, setPassedFilter] = useState<string>('all');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ExamAttemptDetail | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [userIdInput, setUserIdInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  // ID пользователя применяется через 300 мс после ввода; страница — на первую.
  const applyUserId = useDebouncedCallback((v: string) => { setUserIdFilter(v); setPage(1); });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params: any = { page, pageSize: PAGE_SIZE };
      if (kindFilter !== 'all') params.kind = kindFilter;
      if (passedFilter !== 'all') params.passed = passedFilter;
      if (userIdFilter.trim()) params.userId = userIdFilter.trim();
      // Границы дня — по Ташкенту, «по» включает выбранный день целиком.
      if (from) params.from = tashkentDayStartIso(from) ?? undefined;
      if (to) params.to = tashkentDayEndIso(to) ?? undefined;
      const res = await learnerApi.listAttempts(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setLoadError(getErrorMessage(e, 'Не удалось загрузить попытки'));
    } finally {
      setLoading(false);
    }
  }, [page, kindFilter, passedFilter, userIdFilter, from, to]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadDetail = useCallback(async (id: string) => {
    setViewLoading(true);
    setViewError(null);
    try {
      const detail = await learnerApi.getAttempt(id);
      setViewing(detail);
    } catch (e: any) {
      setViewError(getErrorMessage(e, 'Не удалось загрузить попытку'));
    } finally {
      setViewLoading(false);
    }
  }, []);

  const handleView = (id: string) => {
    setViewingId(id);
    setViewing(null);
    void loadDetail(id);
  };

  const closeView = () => {
    setViewingId(null);
    setViewing(null);
    setViewError(null);
  };

  const csvColumns: CsvColumn<ExamAttemptListItem>[] = [
    { header: 'Начато', accessor: (r) => r.started_at },
    { header: 'Завершено', accessor: (r) => r.finished_at },
    { header: 'ID пользователя', accessor: (r) => r.user_id },
    { header: 'Вид', accessor: (r) => labelOf(EXAM_KIND_LABELS, r.kind) },
    { header: 'Режим', accessor: (r) => r.mode },
    { header: 'ID ссылки', accessor: (r) => r.ref_id },
    { header: 'Всего вопросов', accessor: (r) => r.total_questions },
    { header: 'Отвечено', accessor: (r) => r.answered_count },
    { header: 'Верных', accessor: (r) => r.correct_count },
    { header: 'Неверных', accessor: (r) => r.wrong_count },
    { header: 'Время (с)', accessor: (r) => r.time_spent_seconds },
    { header: 'Сдан', accessor: (r) => r.passed },
    { header: 'Брошен', accessor: (r) => r.abandoned },
  ];

  const fetchAllForExport = async (): Promise<ExamAttemptListItem[]> => {
    const rows: ExamAttemptListItem[] = [];
    const params: any = { pageSize: 100 };
    if (kindFilter !== 'all') params.kind = kindFilter;
    if (passedFilter !== 'all') params.passed = passedFilter;
    if (userIdFilter.trim()) params.userId = userIdFilter.trim();
    if (from) params.from = tashkentDayStartIso(from) ?? undefined;
    if (to) params.to = tashkentDayEndIso(to) ?? undefined;
    let pageN = 1;
    // hard cap: 50 pages × 100 = 5000 rows max per export
    while (pageN <= 50) {
      const res = await learnerApi.listAttempts({ ...params, page: pageN });
      rows.push(...res.items);
      if (!res.hasMore) break;
      pageN += 1;
    }
    return rows;
  };

  const formatDate = (s: string | null) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('ru-RU', {
      timeZone: 'Asia/Tashkent',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (secs: number | null) => {
    if (secs == null) return '—';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m} мин ${s} с` : `${s} с`;
  };

  const resetPage = () => setPage(1);
  const chips: FilterChip[] = [];
  if (kindFilter !== 'all')
    chips.push({ key: 'kind', label: `Тип: ${labelOf(EXAM_KIND_LABELS, kindFilter)}`, onRemove: () => { setKindFilter('all'); resetPage(); } });
  if (passedFilter !== 'all')
    chips.push({ key: 'passed', label: `Результат: ${PASSED_LABELS[passedFilter]}`, onRemove: () => { setPassedFilter('all'); resetPage(); } });
  if (userIdFilter.trim())
    chips.push({
      key: 'user',
      label: `Пользователь: ${userIdFilter.trim().slice(0, 8)}…`,
      onRemove: () => { setUserIdInput(''); setUserIdFilter(''); resetPage(); },
    });
  if (from || to)
    chips.push({
      key: 'dates',
      label: `Начато: ${from ? day(from) : '…'} – ${to ? day(to) : '…'}`,
      onRemove: () => { setFrom(''); setTo(''); resetPage(); },
    });
  const resetAll = () => {
    setKindFilter('all');
    setPassedFilter('all');
    setUserIdInput('');
    setUserIdFilter('');
    setFrom('');
    setTo('');
    resetPage();
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Попытки экзаменов"
        subtitle="Все попытки учеников, синхронизированные из приложения: пробные, пересдачи, работа над ошибками, марафоны, билеты и темы"
        actions={<ExportCsvButton fetchRows={fetchAllForExport} columns={csvColumns} filename="exam_attempts" />}
      />

      <Card className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
        <FormField label="Тип" tone="filter">
          {(p) => (
            <Select value={kindFilter} onValueChange={(v) => { setKindFilter(v); resetPage(); }}>
              <SelectTrigger {...p}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все типы</SelectItem>
                {KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {EXAM_KIND_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
        <FormField label="Результат" tone="filter">
          {(p) => (
            <Select value={passedFilter} onValueChange={(v) => { setPassedFilter(v); resetPage(); }}>
              <SelectTrigger {...p}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все результаты</SelectItem>
                <SelectItem value="true">Сдал</SelectItem>
                <SelectItem value="false">Не сдал</SelectItem>
              </SelectContent>
            </Select>
          )}
        </FormField>
        <FormField label="ID пользователя" tone="filter">
          <Input
            placeholder="UUID или его начало"
            className="font-mono"
            value={userIdInput}
            onChange={(e) => { setUserIdInput(e.target.value); applyUserId(e.target.value); }}
          />
        </FormField>
        <FormField label="Начато с" tone="filter">
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); resetPage(); }} />
        </FormField>
        <FormField label="по (включительно)" tone="filter">
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); resetPage(); }} />
        </FormField>
      </Card>

      <Card className="overflow-hidden">
        {loadError ? (
          <ErrorState title="Не удалось загрузить попытки" message={loadError} onRetry={fetchItems} />
        ) : (
          <>
            <Toolbar title={loading ? 'Попытки' : `Найдено ${new Intl.NumberFormat('ru-RU').format(total)}`}>
              <FilterChips chips={chips} onReset={resetAll} />
            </Toolbar>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Начато</TableHead>
                  <TableHead>Пользователь</TableHead>
                  <TableHead>Тип</TableHead>
                  <TableHead>Ссылка</TableHead>
                  <TableHead className="text-right">Балл</TableHead>
                  <TableHead>Результат</TableHead>
                  <TableHead className="text-right">Время</TableHead>
                  <TableHead className="w-[1%]">
                    <span className="sr-only">Подробнее</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }, (_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }, (__, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full max-w-24" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={8}>
                      <EmptyState
                        icon={<ClipboardList />}
                        title="Нет попыток по заданным фильтрам"
                        action={chips.length > 0 ? <Button variant="secondary" onClick={resetAll}>Сбросить фильтры</Button> : undefined}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((a) => {
                    const score = a.total_questions > 0 ? `${a.correct_count}/${a.total_questions}` : '—';
                    const result = resultOf(a);
                    return (
                      <TableRow
                        key={a.id}
                        selected={viewingId === a.id}
                        className="cursor-pointer"
                        onClick={() => handleView(a.id)}
                      >
                        <TableCell className="whitespace-nowrap tabular-nums">{formatDate(a.started_at)}</TableCell>
                        <TableCell>
                          <UserIdLink id={a.user_id} />
                        </TableCell>
                        <TableCell>
                          <StatusPill size="sm" tone={KIND_TONE[a.kind] ?? 'neutral'}>
                            {labelOf(EXAM_KIND_LABELS, a.kind)}
                          </StatusPill>
                        </TableCell>
                        <TableCell className="tabular-nums">{a.ref_id ?? '—'}</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">{score}</TableCell>
                        <TableCell>
                          <StatusPill size="sm" tone={result.tone}>
                            {result.text}
                          </StatusPill>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">{formatDuration(a.time_spent_seconds)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Открыть попытку"
                            onClick={(e) => { e.stopPropagation(); handleView(a.id); }}
                          >
                            <ChevronRight className="text-text-2" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                total={total}
                itemsPerPage={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Sheet open={viewingId !== null} onOpenChange={(o) => !o && closeView()}>
        <SheetContent aria-describedby={undefined}>
          <SheetHeader title="Детали попытки" closeLabel="Закрыть попытку" />
          <SheetBody aria-busy={viewLoading}>
            {viewError ? (
              <ErrorState message={viewError} onRetry={() => viewingId && loadDetail(viewingId)} />
            ) : !viewing ? (
              <div className="flex flex-col gap-3">
                <Skeleton className="h-40 w-full rounded-card" />
                <Skeleton className="h-64 w-full rounded-card" />
              </div>
            ) : (
              <>
                <section className="flex flex-col gap-4 rounded-card border border-border-soft bg-surface p-4 shadow-card">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill tone={KIND_TONE[viewing.kind] ?? 'neutral'}>{labelOf(EXAM_KIND_LABELS, viewing.kind)}</StatusPill>
                    <StatusPill tone={resultOf(viewing).tone}>{resultOf(viewing).text}</StatusPill>
                    <b className="ml-auto font-display text-2xl font-extrabold tabular-nums">
                      {viewing.correct_count}/{viewing.total_questions}
                    </b>
                  </div>
                  <DetailSection title="Обзор">
                    <DetailItem label="Режим">{viewing.mode ?? '—'}</DetailItem>
                    <DetailItem label="ID ссылки">{String(viewing.ref_id ?? '—')}</DetailItem>
                    <DetailItem label="Начато">{formatDate(viewing.started_at)}</DetailItem>
                    <DetailItem label="Завершено">{formatDate(viewing.finished_at)}</DetailItem>
                    <DetailItem label="Затрачено">{formatDuration(viewing.time_spent_seconds)}</DetailItem>
                    <DetailItem label="ID пользователя" mono breakAll>
                      {viewing.user_id}
                    </DetailItem>
                  </DetailSection>
                </section>

                <section className="flex flex-col gap-3 rounded-card border border-border-soft bg-surface p-4 shadow-card">
                  <h3 className="m-0 font-display text-[15px] font-bold">Ответы ({viewing.answers_log.length})</h3>
                  {viewing.answers_log.length === 0 ? (
                    <p className="m-0 text-sm text-text-2">Ответов нет</p>
                  ) : (
                    <ol className="m-0 flex max-h-[400px] list-none flex-col gap-1 overflow-y-auto p-0 text-xs">
                      {viewing.answers_log.map((ans, idx) => (
                        <li
                          key={idx}
                          className={cn(
                            'flex items-center justify-between gap-3 rounded-lg px-2.5 py-2',
                            ans.is_correct ? 'bg-pos-soft' : 'bg-neg-soft',
                          )}
                        >
                          <span className="font-mono">Вопрос #{ans.question_id}</span>
                          <span className="tabular-nums">
                            <span className={cn('mr-2 font-bold', ans.is_correct ? 'text-pos' : 'text-neg')}>
                              {ans.selected || '—'}
                            </span>
                            {!ans.is_correct && <span className="text-text-2">верный: {ans.correct}</span>}
                            <span className="sr-only">{ans.is_correct ? ' — верно' : ' — неверно'}</span>
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </section>

                {viewing.client_meta && (
                  <section className="flex flex-col gap-3 rounded-card border border-border-soft bg-surface p-4 shadow-card">
                    <h3 className="m-0 font-display text-[15px] font-bold">Метаданные клиента</h3>
                    <pre className="m-0 overflow-auto rounded-xl bg-surface-alt p-3 text-xs text-text-2">
                      {JSON.stringify(viewing.client_meta, null, 2)}
                    </pre>
                  </section>
                )}
              </>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}
